import React, { useEffect, useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Table from 'react-bootstrap/Table';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const TIER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'free', label: 'Free tier' },
  { value: 'paid', label: 'Paid only' },
];

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) {
    return 'Not yet';
  }
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch (_error) {
    return value;
  }
};

const difficultyBadge = (d) => {
  if (d === 'starter') return <Badge bg="success">Starter</Badge>;
  if (d === 'advanced') return <Badge bg="danger">Advanced</Badge>;
  return <Badge bg="info">Standard</Badge>;
};

const getLifecycleBadge = (drill) => {
  if (drill.status === 'published') {
    return { className: 'case-lifecycle-badge--live', icon: 'bi-circle-fill', label: 'Published' };
  }
  if (drill.status === 'archived') {
    return { className: 'case-lifecycle-badge--archived', icon: 'bi-archive', label: 'Archived' };
  }
  return { className: 'case-lifecycle-badge--draft', icon: 'bi-circle', label: 'Draft' };
};

const readableTextOn = (hex) => {
  if (typeof hex !== 'string') return '#ffffff';
  const clean = hex.replace('#', '');
  const value = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  if (value.length !== 6) return '#ffffff';
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111827' : '#ffffff';
};

const includesSearchText = (value, searchTerm) => String(value || '').toLowerCase().includes(searchTerm);

const FacilitatorOutpatientLibrary = ({
  drills = [],
  onCreate,
  onEdit,
  onPublish,
  onArchive,
  onDelete,
  onBack,
  user,
  tenant,
}) => {
  const canCurateFreeTier = Boolean(
    tenant?.tenantType === 'central_students'
    && (user?.role === 'platform_admin' || user?.role === 'educator_admin')
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState('title');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'updatedAt' ? 'desc' : 'asc');
    }
  };

  useEffect(() => { setPage(1); }, [search, statusFilter, tierFilter]);

  const sorted = useMemo(() => {
    const sign = sortDir === 'desc' ? -1 : 1;
    return [...drills].sort((left, right) => {
      if (sortKey === 'updatedAt') {
        const ld = left.updatedAt ? new Date(left.updatedAt).getTime() : 0;
        const rd = right.updatedAt ? new Date(right.updatedAt).getTime() : 0;
        return sign * (ld - rd);
      }
      return sign * String(left.title || '').localeCompare(String(right.title || ''), 'en-GB', { sensitivity: 'base' });
    });
  }, [drills, sortKey, sortDir]);

  const searchTerm = search.trim().toLowerCase();
  const filtered = useMemo(() => sorted.filter((d) => {
    if (statusFilter !== 'all' && d.status !== statusFilter) return false;
    if (tierFilter === 'free' && !d.freeTier) return false;
    if (tierFilter === 'paid' && d.freeTier) return false;
    if (!searchTerm) return true;
    return [d.title, d.summary].some((value) => includesSearchText(value, searchTerm));
  }), [sorted, statusFilter, tierFilter, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const paged = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return filtered.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filtered, page]);

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (tierFilter !== 'all' ? 1 : 0);

  return (
    <div className="student-page">
      <Container fluid className="mt-4 mb-5 px-4 student-page__content facilitator-library-shell">
        <FacilitatorPageHeader
          title="Outpatient / clinic case study library"
          description={
            <>
              Clinic-style take-home prescription scenarios. The student writes a prescription on a simulated form using the same library dropdowns the lecturer used in the answer key, so grading stays reliable. CD legal requirements apply automatically when the expected drug is a controlled drug; every form carries a TRAINING SIMULATION banner.
            </>
          }
          onBack={onBack}
          actions={<Button type="button" variant="primary" size="sm" onClick={onCreate}>New outpatient case study</Button>}
        />

        <div className="student-dashboard-section">
          <div className="student-dashboard-section__header">
            <h4 className="mb-1">My outpatient case studies</h4>
            <p className="student-dashboard-header__copy mb-0">
              {drills.length} owned case{drills.length === 1 ? '' : 's'} available.
            </p>
          </div>

          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Group controlId="facilitatorOutpatientSearch" className="mb-0" style={{ width: '240px' }}>
                <Form.Control
                  type="search"
                  size="sm"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search case studies"
                />
              </Form.Group>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowFilters(true)}
              >
                <i className="bi bi-funnel me-1" aria-hidden="true" />
                Filters
                {activeFilterCount ? <Badge bg="primary" className="ms-2">{activeFilterCount}</Badge> : null}
              </Button>
            </div>
            <div className="small text-muted mt-2">
              Showing {filtered.length ? ((page - 1) * PAGE_SIZE) + 1 : 0}
              {' '}-{' '}
              {Math.min(page * PAGE_SIZE, filtered.length)}
              {' '}of {filtered.length}
            </div>
          </div>

          <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>Filter case studies</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <div className="mb-4">
                <div className="small fw-semibold mb-2">Status</div>
                <div className="d-flex gap-2 flex-wrap">
                  {FILTER_OPTIONS.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      size="sm"
                      variant={statusFilter === option.value ? 'primary' : 'outline-secondary'}
                      onClick={() => setStatusFilter(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              {canCurateFreeTier ? (
                <div className="mb-4">
                  <div className="small fw-semibold mb-2">Tier</div>
                  <div className="d-flex gap-2 flex-wrap">
                    {TIER_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        size="sm"
                        variant={tierFilter === option.value ? 'primary' : 'outline-secondary'}
                        onClick={() => setTierFilter(option.value)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="d-flex gap-2">
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => { setStatusFilter('all'); setTierFilter('all'); }}
                >
                  Reset
                </Button>
                <Button type="button" variant="primary" size="sm" onClick={() => setShowFilters(false)}>Apply</Button>
              </div>
            </Offcanvas.Body>
          </Offcanvas>

          {filtered.length ? (
            <>
              <div className="table-responsive">
                <Table borderless hover className="facilitator-library-table facilitator-library-table--drills mb-0">
                  <colgroup>
                    <col className="facilitator-library-col--main" />
                    <col className="facilitator-library-col--meta" />
                    <col className="facilitator-library-col--meta" />
                    <col className="facilitator-library-col--actions" />
                    <col className="facilitator-library-col--publish" />
                    <col className="facilitator-library-col--more" />
                  </colgroup>
                  <thead style={{ color: readableTextOn(tenant?.brandPrimaryColor || '#2563eb') }}>
                    <tr>
                      <th>
                        <button
                          type="button"
                          className="facilitator-library-sort-header"
                          onClick={() => toggleSort('title')}
                          aria-sort={sortKey === 'title' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          Case study
                          <i className={`bi ${sortKey === 'title' ? (sortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up'} ms-1`} aria-hidden="true" />
                        </button>
                      </th>
                      <th className="facilitator-library-cell--status">Availability</th>
                      <th className="facilitator-library-cell--updated">
                        <button
                          type="button"
                          className="facilitator-library-sort-header"
                          onClick={() => toggleSort('updatedAt')}
                          aria-sort={sortKey === 'updatedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        >
                          Updated
                          <i className={`bi ${sortKey === 'updatedAt' ? (sortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up'} ms-1`} aria-hidden="true" />
                        </button>
                      </th>
                      <th>Edit</th>
                      <th>Publish</th>
                      <th className="facilitator-library-cell--more">
                        <span className="visually-hidden">More actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((d) => {
                      const lifecycle = getLifecycleBadge(d);
                      const completionPct = d.totalAttempts > 0 ? Math.round((d.completedAttempts / d.totalAttempts) * 100) : null;
                      const isPublished = d.status === 'published';
                      const isArchived = d.status === 'archived';
                      return (
                        <tr key={d.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                              <div className="fs-5 fw-semibold text-body lh-sm">{d.title}</div>
                              {d.mode === 'verify'
                                ? <Badge bg="info"><i className="bi bi-search me-1" />Verify</Badge>
                                : <Badge bg="primary"><i className="bi bi-pencil-square me-1" />Prescribe</Badge>}
                              {d.hasControlledDrug ? <Badge bg="warning" text="dark">Includes CD</Badge> : null}
                            </div>
                            <div className="text-muted mb-1" style={{ fontSize: '0.875rem' }}>
                              {d.summary || 'No description added yet.'}
                            </div>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {difficultyBadge(d.difficulty)}
                              {d.mode === 'verify' ? (
                                <Badge bg="light" text="dark">
                                  {d.verifyDrugCount || 0} drug{d.verifyDrugCount === 1 ? '' : 's'} · {d.verifyErrorCount || 0} error{d.verifyErrorCount === 1 ? '' : 's'}
                                </Badge>
                              ) : (
                                <Badge bg="light" text="dark">
                                  {d.expectedSlotCount || 0} answer slot{d.expectedSlotCount === 1 ? '' : 's'}
                                </Badge>
                              )}
                            </div>
                            {d.totalAttempts > 0 ? (
                              <div className="small text-muted mt-2">
                                <strong>{d.totalAttempts}</strong> attempt{d.totalAttempts === 1 ? '' : 's'} · <strong>{d.uniqueStudents}</strong> student{d.uniqueStudents === 1 ? '' : 's'}
                                {' · '}{d.completedAttempts}/{d.totalAttempts} completed
                                {completionPct != null ? ` (${completionPct}%)` : ''}
                                {d.avgOverallPct != null ? <> · avg <strong>{Math.round(d.avgOverallPct)}%</strong></> : null}
                              </div>
                            ) : null}
                          </td>
                          <td className="facilitator-library-cell--status">
                            <div className="facilitator-library-status text-center">
                              <div className="d-flex flex-column align-items-center gap-2">
                                <span className={`case-lifecycle-badge ${lifecycle.className}`}>
                                  <i className={`bi ${lifecycle.icon} me-1`} aria-hidden="true" />
                                  {lifecycle.label}
                                </span>
                                {canCurateFreeTier ? (
                                  <Badge bg={d.freeTier ? 'success' : 'secondary'}>
                                    <i className={`bi ${d.freeTier ? 'bi-unlock-fill' : 'bi-lock-fill'} me-1`} aria-hidden="true" />
                                    {d.freeTier ? 'Free' : 'Paid'}
                                  </Badge>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="small facilitator-library-cell--updated">{formatDateTime(d.updatedAt)}</td>
                          <td>
                            <div className="facilitator-library-actions-row">
                              <button
                                type="button"
                                className="facilitator-library-action-pill facilitator-library-action-pill--edit"
                                onClick={() => onEdit?.(d)}
                              >
                                <i className="bi bi-pencil-square me-1" aria-hidden="true" /> Edit case
                              </button>
                            </div>
                          </td>
                          <td>
                            {isArchived ? (
                              <span className="text-muted small">Archived</span>
                            ) : (
                              <button
                                type="button"
                                className={`facilitator-library-action-pill ${isPublished ? 'facilitator-library-action-pill--update' : 'facilitator-library-action-pill--publish'}`}
                                onClick={() => onPublish?.(d.id)}
                              >
                                <i className={`bi ${isPublished ? 'bi-arrow-repeat' : 'bi-upload'} me-1`} aria-hidden="true" />
                                {isPublished ? 'Update' : 'Publish'}
                              </button>
                            )}
                          </td>
                          <td className="facilitator-library-cell--more">
                            <Dropdown className="facilitator-library-more-wrap">
                              <Dropdown.Toggle as="button" type="button" className="facilitator-library-more-toggle" aria-label="More actions" title="More actions">
                                <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                {!isArchived ? (
                                  <Dropdown.Item onClick={() => onArchive?.(d.id)}>
                                    <i className="bi bi-archive-fill me-2" aria-hidden="true" /> Archive
                                  </Dropdown.Item>
                                ) : null}
                                <Dropdown.Item className="text-danger" onClick={() => onDelete?.(d.id)}>
                                  <i className="bi bi-trash-fill me-2" aria-hidden="true" /> Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              {pageCount > 1 ? (
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
                  <div className="small text-muted">Page {page} of {pageCount}</div>
                  <div className="d-flex gap-2 flex-wrap">
                    <Button type="button" size="sm" variant="outline-secondary" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1}>
                      Previous
                    </Button>
                    <Button type="button" size="sm" variant="outline-secondary" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} disabled={page === pageCount}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="student-dashboard-empty">
              <i className="bi bi-folder2-open" aria-hidden="true" />
              {drills.length ? (
                <>
                  <div className="mb-2">No outpatient case studies match your filters or search.</div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => { setStatusFilter('all'); setTierFilter('all'); setSearch(''); }}
                  >
                    Clear filters
                  </Button>
                </>
              ) : (
                <div>No outpatient case studies in your workspace yet. Click <strong>New outpatient case study</strong> to author one, or ask the platform admin to seed the curated library on this tenant.</div>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};

export default FacilitatorOutpatientLibrary;
